/*
 * The reviews sort dropdown, restyled to match every other dropdown on the
 * site (Jenny 2026-09-28).
 *
 * WHY THIS EXISTS AT ALL
 * A native <select> opens a list painted by the operating system. CSS cannot
 * touch it. So the reviews sort was the one control still showing a grey
 * square system menu while the collection sort and the variant picker both
 * wore the Figma component. The only fix is to show our own list.
 *
 * WHY IT IS NOT custom-new.js
 * That builder wraps a <select> in new elements and moves the select inside
 * them. Fine for the theme's own markup. Not fine here: this select belongs
 * to Judge.me, a Vue app that re-renders the whole widget every time reviews
 * load, filter or re-sort. Re-parenting a node that a framework is tracking
 * is how you get a control that vanishes mid-click or appears twice.
 *
 * So nothing of theirs is moved, wrapped or replaced. This script:
 *   - leaves their <select> exactly where it is, as the real control,
 *   - appends ONE panel to <body>, outside their tree, where no re-render can
 *     reach it,
 *   - intercepts only the MOUSE press that would open the system menu.
 *
 * ACCESSIBILITY
 * The <select> is untouched, so it stays focusable, stays in the tab order,
 * and keeps its label and its screen reader behaviour. Keyboard users get the
 * real control and the native menu, which is the behaviour they expect; only
 * a mouse press is redirected. Choosing a row writes to the select and fires
 * a real `change`, which is the same event Judge.me already listens for, so
 * the reviews re-sort exactly as before.
 */
(function () {
  var SELECT = 'select.jm-sort-filter__select';
  var openPanel = null;

  function closeOpen() {
    if (!openPanel) return;
    openPanel.classList.remove('is-open');
    openPanel = null;
  }

  function place(panel, anchor) {
    var r = anchor.getBoundingClientRect();
    /* Measured while hidden but laid out, so the width is real. */
    var w = panel.offsetWidth;
    var gap = 8;

    /* The control sits at the right edge of the reviews header, so the panel
       hangs from its right edge. Clamped to the viewport either way, which
       matters on a phone where the panel is wider than the button by a lot. */
    var left = r.right - w;
    if (left < gap) left = gap;
    if (left + w > window.innerWidth - gap) left = Math.max(gap, window.innerWidth - gap - w);

    var top = r.bottom + gap;
    /* Flip above the control if there is not room below it. */
    if (top + panel.offsetHeight > window.innerHeight - gap && r.top - gap - panel.offsetHeight > 0) {
      top = r.top - gap - panel.offsetHeight;
    }

    panel.style.left = left + 'px';
    panel.style.top = top + 'px';
  }

  function syncSelection(select, panel) {
    panel.querySelectorAll('.custom-option').forEach(function (row) {
      row.classList.toggle('selection', row.dataset.value === select.value);
    });
  }

  function build(select) {
    if (select.dataset.fjSortInit) return;
    select.dataset.fjSortInit = 'true';

    var panel = document.createElement('div');
    /* fj-sort-list marks the panels THIS script owns. The clean-up below
       used to sweep every .custom-options--portal, which silently deleted the
       reviews FILTER list the moment it opened. */
    panel.className = 'custom-options custom-options--portal fj-sort-list';

    select.querySelectorAll('option').forEach(function (opt) {
      var row = document.createElement('span');
      row.className = 'custom-option';
      row.dataset.value = opt.value;
      /* textContent, never innerHTML: these strings come from an app, and one
         of them one day containing markup should not become a script tag. */
      row.textContent = opt.textContent.trim();
      row.addEventListener('click', function () {
        select.value = opt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        syncSelection(select, panel);
        closeOpen();
      });
      panel.appendChild(row);
    });

    document.body.appendChild(panel);
    syncSelection(select, panel);

    /* mousedown is the press that opens the system menu, so that is the one
       to swallow. Leaving click alone would let the native list flash open
       first. */
    select.addEventListener('mousedown', function (event) {
      event.preventDefault();

      if (openPanel === panel) {
        closeOpen();
        return;
      }

      closeOpen();
      syncSelection(select, panel);
      place(panel, select);
      panel.classList.add('is-open');
      openPanel = panel;
      document.dispatchEvent(new CustomEvent('fj:dropdown-open', { detail: panel }));
    });

    /* Keyboard use goes through the native menu and never opens this panel,
       but the panel must still agree with the select afterwards. */
    select.addEventListener('change', function () {
      syncSelection(select, panel);
      closeOpen();
    });

    select.fjSortPanel = panel;
  }

  function initAll() {
    document.querySelectorAll(SELECT).forEach(build);
  }

  /* The widget arrives late (Judge.me loads it after the page) and re-renders
     on every sort and filter, so one pass at load is not enough. A debounced
     observer re-initialises anything new and drops panels whose select has
     been removed, which is what stops them piling up. */
  var pending = null;
  function onMutate() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      document.querySelectorAll('.fj-sort-list').forEach(function (panel) {
        var stillThere = [].some.call(document.querySelectorAll(SELECT), function (s) {
          return s.fjSortPanel === panel;
        });
        if (!stillThere) {
          if (openPanel === panel) openPanel = null;
          panel.remove();
        }
      });
      initAll();
    }, 120);
  }

  function start() {
    initAll();
    if ('MutationObserver' in window) {
      new MutationObserver(onMutate).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('click', function (event) {
    if (!openPanel) return;
    if (openPanel.contains(event.target)) return;
    if (event.target.closest && event.target.closest(SELECT)) return;
    closeOpen();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeOpen();
  });

  /* Only one dropdown open on the whole page (Jenny 2026-09-29). Every
     dropdown (this one, the reviews filter, the reviews sort, the theme's
     custom selects) announces 'fj:dropdown-open' when it opens and closes
     itself when it hears another one's. A shared signal, because each
     script's own close-on-outside-click can be skipped: the filter button
     swallows its click before the page sees it, which left the sort list
     open underneath the filter list. */
  document.addEventListener('fj:dropdown-open', function (event) {
    if (openPanel && event.detail !== openPanel) closeOpen();
  });

  /* A fixed panel does not travel with the page, so it is dismissed rather
     than left floating away from the button it belongs to. */
  window.addEventListener('scroll', closeOpen, { passive: true });
  window.addEventListener('resize', closeOpen);
})();
